import {
  useCallback, useEffect, useMemo, useState, useSyncExternalStore,
} from 'react';
import { getGraphQlClient } from '../graphql/graphqlClient';
import { canAccessCropPlanningQuery } from '../graphql/queries/canAccessCropPlanning';
import { ImpersonationMode } from '../types/impersonationModes';
import { getCanAccessCropPlanningStore } from '../utils/canAccessCropPlanningStore';
import { reportError } from '../utils/datadog';
import { useImpersonationMode } from './useImpersonationMode';

interface UseCanAccessCropPlanningResult {
  canAccess: boolean;
  isLoading: boolean;
}

/**
 * Deferred hook for crop planning nav access.
 *
 * Runs independently from the main nav loading pipeline so it doesn't
 * block other navigation items from rendering. Reads a preserved localStorage
 * store to show the Plans tab instantly on return visits, with a background
 * revalidation.
 */

// Skipping query check for BR/AR due to a known BE issue with permission checks in those countries.
// Currently not using Crop Planning features in those markets
// This is a temporary measure until the issue is resolved.
const RESTRICTED_COUNTRY_CODES = ['BR', 'AR'];

export const useCanAccessCropPlanning = (): UseCanAccessCropPlanningResult => {
  const { user: loggedInUser, impersonatedUser, impersonationModes } = useImpersonationMode();
  const user = impersonationModes.includes(ImpersonationMode.EMPLOYEE_PROXYING_USER) ? impersonatedUser : loggedInUser;
  const userId = user?.details?.uuid;
  const countryCode = user?.details?.countryCode;

  const [isLoading, setIsLoading] = useState(true);

  const isAllowedCountry = countryCode && !RESTRICTED_COUNTRY_CODES.includes(countryCode);

  const store = useMemo(
    () => (userId && isAllowedCountry ? getCanAccessCropPlanningStore(userId) : undefined),
    [userId, isAllowedCountry],
  );

  const subscribe = useCallback(
    (listener: () => void) => (store ? store.subscribe(listener) : () => {}),
    [store],
  );
  const getSnapshot = useCallback(() => store?.getSnapshot() ?? null, [store]);

  const cachedAccess = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const fetchAccess = useCallback(
    async (uid: string) => {
      try {
        // Always revalidate against the server to pick up access changes
        const client = getGraphQlClient({ isUApi: true });
        const { data } = await client.query({
          query: canAccessCropPlanningQuery,
          variables: { userId: uid },
          fetchPolicy: 'network-only',
        });

        getCanAccessCropPlanningStore(uid).set(!!data?.canAccessCropPlanning);
      } catch (error) {
        // On error the cached value stands; an empty store falls back to false
        reportError('FieldViewCropPlanningAccessError', error, { hook: 'useCanAccessCropPlanning', userId: uid });
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!userId || !isAllowedCountry) {
      setIsLoading(false);
      return;
    }

    fetchAccess(userId);
  }, [userId, fetchAccess, isAllowedCountry]);

  return {
    canAccess: cachedAccess ?? false,
    isLoading,
  };
};
